import { useState } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../../misc/Card";
import { Label } from "../../misc/Label";
import { RadioGroup, RadioGroupItem } from "../../misc/RadioGroup";
import { Textarea } from "../../misc/Textarea";
import { Button } from "../../misc/Button";
import { TabsContent } from "../../misc/Tabs";
import { FaBug, FaLightbulb, FaQuestion, FaUserLock } from "react-icons/fa6";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import axios from "axios";
import { getAuthHeader } from "../../../utils/authHeader";

const issueTypes = [
    { value: "Bug Report", label: "Bug Report", icon: <FaBug /> },
    { value: "Feature Request", label: "Feature Request", icon: <FaLightbulb /> },
    { value: "Account Issue", label: "Account Issue", icon: <FaUserLock /> },
    { value: "Other", label: "Other", icon: <FaQuestion /> },
];

export default function ContactForm() {
    const [issueType, setIssueType] = useState("");
    const [description, setDescription] = useState("");
    const [feedback, setFeedback] = useState<{
        type: "success" | "error";
        message: string;
    } | null>(null);

    const user = useSelector((state: RootState) => state.auth.user);

    // const [attachment, setAttachment] = useState<File | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!issueType || !description) {
            setFeedback({
                type: "error",
                message: "Please fill out all required fields.",
            });
            return;
        }
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
            const headers = await getAuthHeader();

            // Who is writing is the verified sign-in on the request; the api never reads a
            // user id from the form. The profile name and email go along only when set.
            const userName = `${user?.nameFirst || ""} ${user?.nameLast || ""}`.trim();
            const payload = {
                ...(userName ? { userName } : {}),
                ...(user?.email ? { userEmail: user.email } : {}),
                issueType,
                description,
                subjectLine: `New Support Ticket: ${issueType}`,
                formOrigination: "Marabot",
            };

            await axios.post(`${backendUrl}/contact/support`, payload, {
                headers: { ...headers, "Content-Type": "application/json" },
            });

            setFeedback({
                type: "success",
                message:
                    "Your request has been submitted. Our team will get back to you shortly.",
            });

            // Reset form
            setIssueType("");
            setDescription("");
        } catch (err) {
            console.error("Failed to submit support request", err);
            setFeedback({
                type: "error",
                message:
                    "Failed to submit your request. Please try again or contact us at support@marabot.ai",
            });
        }
    };

    return (
        <TabsContent value="contact">
            <Card className="w-full max-w-2xl mx-auto bg-gradient-to-br from-gray-50 to-gray-100 shadow-lg">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-3xl font-bold text-center">
                        How Can We Help?
                    </CardTitle>
                    <CardDescription className="text-center text-lg">
                        We're here to assist you with any issues or suggestions.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-6">
                        {feedback && (
                            <div
                                className={`border rounded-md p-3 text-sm ${
                                    feedback.type === "success"
                                        ? "bg-green-50 border-green-200 text-green-700"
                                        : "bg-red-50 border-red-200 text-red-700"
                                }`}
                                role="alert"
                                aria-live="polite"
                            >
                                {feedback.message}
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label
                                htmlFor="issue-type"
                                className="text-lg font-semibold text-primary"
                            >
                                What's this about?
                            </Label>
                            <RadioGroup
                                value={issueType}
                                onValueChange={setIssueType}
                                className="grid grid-cols-2 gap-4"
                            >
                                {issueTypes.map((type) => (
                                    <Label
                                        key={type.value}
                                        htmlFor={type.value}
                                        className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all duration-200 ease-in-out ${
                                            issueType === type.value
                                                ? "bg-secondary text-white shadow-md scale-105"
                                                : "bg-background text-primary hover:bg-lightSecondary"
                                        }`}
                                    >
                                        <RadioGroupItem
                                            value={type.value}
                                            id={type.value}
                                            className="sr-only"
                                        />
                                        <span className="text-2xl">
                                            {type.icon}
                                        </span>
                                        <span className="font-semibold">
                                            {type.label}
                                        </span>
                                    </Label>
                                ))}
                            </RadioGroup>
                        </div>
                        <div className="space-y-2">
                            <Label
                                htmlFor="description"
                                className="text-lg font-semibold text-primary"
                            >
                                Tell us more
                            </Label>
                            <Textarea
                                id="description"
                                placeholder="Please provide details about your issue or suggestion..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                required
                                className="min-h-[150px] text-base resize-none"
                            />
                        </div>
                    </CardContent>
                    <CardFooter>
                        <Button type="submit" className="w-full text-lg h-12">
                            Next
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </TabsContent>
    );
}
